import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation.ts';
import type { SupplementalAdQuality } from '@openx/types/deal.ts';
import { useBrandsFetch } from '@openx/utils/apiHooks/useBrandsFetch.ts';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDealContext } from '../../utils/context/DealContext.tsx';

import { SelectedFilters } from './components/SelectedFilters.tsx';
import { Filters, getFilterOptions, getLeanFilters } from './constants.ts';
import { mapToFormState } from './utils.ts';

const StyledRemoveButton = styled(Button)`
  color: ${({ theme }) => theme.palette.error.main};
  margin-left: ${({ theme }) => theme.spacing(2)};

  :hover {
    color: ${({ theme }) => theme.palette.error.dark};
  }
`;

const StyledContainerPaper = styled(Paper)`
  box-shadow: ${ELEVATION.elevation0};
  margin: ${({ theme }) => theme.spacing(3, 0)};
  padding: ${({ theme }) => theme.spacing(2, 3)};
`;

interface SupplementalAdQualityDetailsProps {
  currentState: SupplementalAdQuality | null;
  readonly?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
  isSubmitting?: boolean;
}

function getTitle(title, filters) {
  return filters === 0 ? title : `${title} [ ${filters} ]`;
}

export const SupplementalAdQualityDetails = memo(function SupplementalAdQualityDetails(
  props: SupplementalAdQualityDetailsProps
) {
  const { useContentAttributesFetch, useCreativeTypesFetch, useLanguagesFetch, useMarketBrandGroupsFetch } =
    useDealContext();

  const { currentState, readonly, onRemove, onEdit, isSubmitting } = props;
  const { t } = useTranslation();
  const details = useMemo(() => mapToFormState(currentState), [currentState]);
  const filterOptions = useMemo(() => getFilterOptions(t), [t]);
  const leanFilters = useMemo(() => getLeanFilters(t), [t]);
  const { isBrandsLoading, brands = {} } = useBrandsFetch([...details.brands.ids]);
  const { isContentAttributesLoading, contentAttributes, refetchContentAttributes } = useContentAttributesFetch();
  const { isCreativeTypesLoading, creativeTypes, refetchCreativeTypes } = useCreativeTypesFetch();
  const { marketBrandGroupsLoading, refetchMarketBrandGroups, marketBrandGroups } = useMarketBrandGroupsFetch();
  const { isLanguagesLoading, languages, refetchLanguages } = useLanguagesFetch();

  useEffect(() => {
    // fetch options only when needed
    if (details.creative_types.op === Filters.ALLOW) refetchCreativeTypes();
    if (details.content_attributes.op === Filters.ALLOW) refetchContentAttributes();
    if (details.brand_labels.op === Filters.ALLOW) refetchMarketBrandGroups();
    if (details.languages.op === Filters.ALLOW) refetchLanguages();
  }, [refetchCreativeTypes, details, refetchContentAttributes, refetchMarketBrandGroups, refetchLanguages]);

  // use op to prevent loader when loading options in drawer
  const isOptionsLoading =
    (isBrandsLoading && details.brands.op === Filters.ALLOW) ||
    (isContentAttributesLoading && details.content_attributes.op === Filters.ALLOW) ||
    (isCreativeTypesLoading && details.creative_types.op === Filters.ALLOW) ||
    (marketBrandGroupsLoading && details.brand_labels.op === Filters.ALLOW) ||
    (isLanguagesLoading && details.languages.op === Filters.ALLOW);

  if (!currentState) {
    return null;
  }

  if (isOptionsLoading) {
    return (
      <StyledContainerPaper variant="outlined" square>
        <Loader />
      </StyledContainerPaper>
    );
  }

  const allowLabel = details.allow_unbranded_rtb_bids ? t('Allow') : t('Block');

  return (
    <StyledContainerPaper variant="outlined" square>
      <Grid
        container
        wrap="nowrap"
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Typography data-test="supplemental-ad-quality-filters" variant="h3">
          {t('Supplemental Ad Quality Filters')}
        </Typography>
        {!readonly && (
          <Grid>
            <Button type="button" variant="text" color="primary" onClick={onEdit} disabled={isSubmitting}>
              {t('Edit')}
            </Button>
            <StyledRemoveButton
              type="button"
              variant="text"
              color="secondary"
              onClick={onRemove}
              disabled={isSubmitting}
            >
              {t('Remove')}
            </StyledRemoveButton>
          </Grid>
        )}
      </Grid>
      <SelectedFilters
        selectedOptions={details.brand_labels.ids}
        options={marketBrandGroups}
        title={getTitle(t('Industries'), details.brand_labels.ids.length)}
        filtersTitle={filterOptions[details.brand_labels.op].name}
        readonly
      />
      <SelectedFilters
        selectedOptions={details.brands.ids}
        options={brands}
        title={getTitle(t('Brands'), details.brands.ids.length)}
        filtersTitle={filterOptions[details.brands.op].name}
        additionalFiltersInfo={t('{allowLabel} Unbranded RTB Bids', { allowLabel }) as string}
        readonly
      />
      <SelectedFilters
        selectedOptions={details.content_attributes.ids}
        options={contentAttributes}
        title={getTitle(t('Content Attributes'), details.content_attributes.ids.length)}
        filtersTitle={filterOptions[details.content_attributes.op].name}
        readonly
      />
      <SelectedFilters
        selectedOptions={details.creative_types.ids}
        options={creativeTypes}
        title={getTitle(t('Creative Types'), details.creative_types.ids.length)}
        filtersTitle={filterOptions[details.creative_types.op].name}
        readonly
      />
      <SelectedFilters
        selectedOptions={details.languages.ids}
        options={languages}
        title={getTitle(t('Languages'), details.languages.ids.length)}
        filtersTitle={filterOptions[details.languages.op].name}
        readonly
      />
      {details.lean_filters.disable_total_requests_filter && (
        <SelectedFilters
          title={t('Request Limit')}
          filtersTitle={leanFilters[details.lean_filters.disable_total_requests_filter].name}
          readonly
        />
      )}
      {details.lean_filters.disable_pixel_buckets && (
        <SelectedFilters
          title={t('File Weight')}
          filtersTitle={leanFilters[details.lean_filters.disable_pixel_buckets].name}
          readonly
        />
      )}
    </StyledContainerPaper>
  );
});
